import { useEffect, useMemo } from "react"
import { MapContainer, Marker, useMap, useMapEvents } from "react-leaflet"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { MapTiles, MAP_MAX_ZOOM } from "../../map/components/map-tiles"

export interface LatLng {
    lat: number;
    lng: number;
}

const DEFAULT_CENTER: LatLng = {lat: 55.7522, lng: 37.6156}
const PICK_ZOOM = 16

// Формат location на бэке — "lat,lon"
export const parseLocation = (location?: string | null): LatLng | null => {
    const [lat, lng] = (location ?? '').split(',').map(Number)
    return Number.isFinite(lat) && Number.isFinite(lng) ? {lat, lng} : null
}

// update-store-location принимает только ссылку — собираем ссылку Яндекс Карт с меткой (pt=долгота,широта)
export const toYandexMapsLink = ({lat, lng}: LatLng) =>
    `https://yandex.ru/maps/?pt=${lng.toFixed(6)},${lat.toFixed(6)}&z=${PICK_ZOOM}&l=map`

const pinIcon = L.divIcon({
    className: 'admin-location-pin',
    html: '<span></span>',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
})

const ClickHandler = ({onPick}: {onPick: (point: LatLng) => void}) => {
    useMapEvents({click: (e) => onPick({lat: e.latlng.lat, lng: e.latlng.lng})})
    return null
}

// Без точки центрируем карту на пользователе
const LocateUser = () => {
    const map = useMap()
    useEffect(() => {
        navigator.geolocation?.getCurrentPosition(
            ({coords}) => map.setView([coords.latitude, coords.longitude], PICK_ZOOM - 2),
            () => {},
            {timeout: 10000},
        )
    }, [map])
    return null
}

interface LocationPickerProps {
    initial: LatLng | null;
    point: LatLng | null;
    onPick: (point: LatLng) => void;
}

export const LocationPicker = ({initial, point, onPick}: LocationPickerProps) => {
    // Центр задаём один раз при открытии, дальше карту двигает пользователь
    const center = useMemo(() => point ?? initial ?? DEFAULT_CENTER, [])  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <MapContainer
            className="admin-location-map"
            center={[center.lat, center.lng]}
            zoom={point || initial ? PICK_ZOOM : 12}
            maxZoom={MAP_MAX_ZOOM}
            zoomControl={false}
            attributionControl={false}
        >
            <MapTiles />
            <ClickHandler onPick={onPick} />
            {!point && !initial && <LocateUser />}
            {point && <Marker position={[point.lat, point.lng]} icon={pinIcon} />}
            {!point && initial && <Marker position={[initial.lat, initial.lng]} icon={pinIcon} opacity={0.5} />}
        </MapContainer>
    )
}
